import React, { useState, useEffect } from 'react';
import { supabase } from './supabase';
import { toastSuccess, toastError } from './ui/toast';
import ConfirmDialog from './ui/ConfirmDialog';
import { useUserRole } from './hooks/useUserRole';
import CipherHeader from './components/ui/CipherHeader';
import { cloneProgram as cloneProgramService } from './services/programCloneService';
import { splitTemplateLibrary, isArchived } from './utils/templateLibrary';
import { colors as theme } from './theme';

const ProgramsList = ({ onSelectProgram, onGeneratePDF, onBack, onEditProgram }) => {
  const [programs, setPrograms] = useState([]);
  const [activeTab, setActiveTab] = useState('templates'); // 'templates' | 'extra' | 'client'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, programId: null });
  const { role, user } = useUserRole();

  useEffect(() => {
    if (role && user) loadPrograms();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, user]);

  const loadPrograms = async () => {
    if (!role || !user) return;
    try {
      setLoading(true);
      setError(null);

      let allowedAuthUserIds = null; // null = no filter (super_admin sees all)

      if (role === 'coach') {
        allowedAuthUserIds = [user.auth_user_id];
      } else if (role === 'admin') {
        const { data: myCoaches } = await supabase
          .from('users')
          .select('auth_user_id')
          .eq('admin_id', user.auth_user_id)
          .eq('role', 'coach');
        const coachAuthIds = (myCoaches || []).map(c => c.auth_user_id).filter(Boolean);
        allowedAuthUserIds = [user.auth_user_id, ...coachAuthIds];
      }
      // super_admin: allowedAuthUserIds stays null → no filter

      let query = supabase
        .from('programs')
        .select('*')
        .eq('is_client_copy', false)
        .order('created_at', { ascending: false });

      if (allowedAuthUserIds) {
        // Show templates + programs created by this coach/admin's users
        query = query.or(`is_template.eq.true,created_by.in.(${allowedAuthUserIds.join(',')})`);
      }

      const { data, error: programsError } = await query;

      if (programsError) throw programsError;

      const safeData = data || [];

      // Get onboarding data only for users referenced in these programs
      const programUserIds = [...new Set(safeData.map(p => p.user_id).filter(Boolean))];

      let clientsData = [];
      if (programUserIds.length > 0) {
        const { data: fetchedClients, error: clientsError } = await supabase
          .from('onboarding_data')
          .select('user_id, full_name, email')
          .in('user_id', programUserIds);

        if (clientsError) {
          console.warn('Could not load client data:', clientsError);
        } else {
          clientsData = fetchedClients || [];
        }
      }

      // Map client names to programs
      const programsWithClients = safeData.map(program => {
        const client = clientsData.find(c => c.user_id === program.user_id);
        return {
          ...program,
          client_name: client ? client.full_name : null,
          client_email: client ? client.email : null
        };
      });

      // Separate templates from client-specific programs at load time
      setPrograms(programsWithClients);
    } catch (err) {
      console.error('Error loading programs:', err);
      setError('Error loading programs: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const cloneProgram = async (program, openAfter = false) => {
    try {
      const newName = `Copy of ${program.name}`;
      const newId = await cloneProgramService({
        programId: program.id,
        newName,
        userId: program.user_id,
      });
      toastSuccess(`"${newName}" created successfully`);
      loadPrograms();
      if (openAfter) onEditProgram?.(newId, 0);
      return newId;
    } catch (err) {
      console.error('Clone error:', err);
      toastError('Could not clone program');
    }
  };

  const handleEditClick = (program) => {
    // Library programs are always safe to edit — active clients are on their own
    // cloned copies (is_client_copy: true), never on the original template.
    onEditProgram?.(program.id, 0);
  };


  const deleteProgram = (programId) => {
    setConfirmDialog({ isOpen: true, programId });
  };

  const handleConfirmDelete = async () => {
    const { programId } = confirmDialog;
    try {
      // Delete in reverse order of dependencies
      // 1. Get day IDs
      const { data: weeks } = await supabase
        .from('program_weeks')
        .select('id')
        .eq('program_id', programId);

      const weekIds = weeks?.map(w => w.id) || [];

      if (weekIds.length > 0) {
        const { data: days } = await supabase
          .from('program_days')
          .select('id')
          .in('week_id', weekIds);

        const dayIds = days?.map(d => d.id) || [];

        if (dayIds.length > 0) {
          // Delete block_items and exercise_blocks (block-based programs)
          const { data: blocks } = await supabase
            .from('exercise_blocks')
            .select('id')
            .in('day_id', dayIds);

          const blockIds = blocks?.map(b => b.id) || [];
          if (blockIds.length > 0) {
            await supabase.from('block_items').delete().in('block_id', blockIds);
            await supabase.from('exercise_blocks').delete().in('id', blockIds);
          }

          // Delete program_exercises
          await supabase
            .from('program_exercises')
            .delete()
            .in('day_id', dayIds);
        }

        // Delete program_days
        await supabase
          .from('program_days')
          .delete()
          .in('week_id', weekIds);
      }

      // Delete program_weeks
      await supabase
        .from('program_weeks')
        .delete()
        .eq('program_id', programId);

      // Delete program
      const { error: programError } = await supabase
        .from('programs')
        .delete()
        .eq('id', programId);

      if (programError) throw programError;

      // Reload programs
      await loadPrograms();
      toastSuccess('Program deleted successfully!');
    } catch (err) {
      console.error('ProgramsList: Error deleting program:', err);
      toastError('Error deleting program. Please try again.');
    } finally {
      setConfirmDialog({ isOpen: false, programId: null });
    }
  };

  if (loading) {
    return (
      <div style={{ 
        padding: '40px',
        textAlign: 'center',
        color: theme.textMuted
      }}>
        Loading programs...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: '40px',
        backgroundColor: theme.danger,
        color: 'white',
        borderRadius: '8px',
        margin: '20px'
      }}>
        {error}
      </div>
    );
  }

  const isCoachView = role === 'coach';
  const myAuthId = user?.auth_user_id;

  // Coaches get their own creations (templated or not) kept out of the
  // shared Template Library tab entirely — RLS already enforces this for
  // other coaches' programs; this mirrors it for the coach's own so their
  // template-flagged programs don't sit alongside the curated library.
  // An archived program has been withdrawn: it is not listed on either tab.
  const live = programs.filter(p => !isArchived(p));
  const templates = isCoachView
    ? live.filter(p => p.is_template && p.created_by !== myAuthId)
    : live.filter(p => p.is_template);
  const clientPrograms = isCoachView
    ? live.filter(p => p.created_by === myAuthId)
    : live.filter(p => !p.is_template);
  // The templates are in two folders. The Template Library is the core set, the one auto-assign
  // hands new clients, shown in groups by where the client trains and what they are after. Extra
  // Programs is every other template: assigned by hand, never automatically. The last tab is one
  // list, as it was.
  const { core: coreGroups, extra: extraPrograms } = splitTemplateLibrary(templates);
  const coreCount = coreGroups.reduce((n, group) => n + group.programs.length, 0);
  const sections = activeTab === 'templates' ? coreGroups
    : activeTab === 'extra' ? [{ key: 'extra', label: null, programs: extraPrograms }]
    : [{ key: 'all', label: null, programs: clientPrograms }];
  const visiblePrograms = sections.flatMap(section => section.programs);
  const secondTabLabel = isCoachView ? 'My Programs' : 'Client Programs';

  return (
    <div style={{
      padding: '40px',
      backgroundColor: theme.bg,
      minHeight: '100vh'
    }}>
      <CipherHeader
        title="Programs Library"
        subtitle="View & manage programs"
        onBack={onBack}
      />

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
        {[
          { key: 'templates', label: `Template Library (${coreCount})` },
          { key: 'extra',     label: `Extra Programs (${extraPrograms.length})` },
          { key: 'client',    label: `${secondTabLabel} (${clientPrograms.length})` },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '10px 20px',
              borderRadius: '8px',
              border: activeTab === tab.key ? `2px solid ${theme.gold}` : `1px solid ${theme.surface3}`,
              background: activeTab === tab.key ? 'rgba(212,175,55,0.1)' : theme.surface1,
              color: activeTab === tab.key ? theme.gold : theme.textMuted,
              fontWeight: activeTab === tab.key ? '700' : '400',
              fontSize: '14px',
              cursor: 'pointer',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Stats */}
      <div style={{
        backgroundColor: theme.surface1,
        padding: '20px',
        borderRadius: '8px',
        marginBottom: '30px',
        border: `1px solid ${theme.surface3}`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        {activeTab === 'templates' ? (
          <>
            <div>
              <div style={{ color: theme.textMuted, fontSize: '14px', marginBottom: '5px' }}>Template Library</div>
              <div style={{ color: theme.gold, fontSize: '32px', fontWeight: 700 }}>{coreCount}</div>
            </div>
            <div>
              <div style={{ color: theme.textMuted, fontSize: '14px', marginBottom: '5px' }}>Auto-assigned to new clients</div>
              <div style={{ color: theme.success, fontSize: '32px', fontWeight: 700 }}>{coreCount}</div>
            </div>
            <div>
              <div style={{ color: theme.textMuted, fontSize: '14px', marginBottom: '5px' }}>Client copies</div>
              <div style={{ color: theme.textMuted, fontSize: '32px', fontWeight: 700 }}>hidden</div>
            </div>
          </>
        ) : activeTab === 'extra' ? (
          <>
            <div>
              <div style={{ color: theme.textMuted, fontSize: '14px', marginBottom: '5px' }}>Extra Programs</div>
              <div style={{ color: theme.gold, fontSize: '32px', fontWeight: 700 }}>{extraPrograms.length}</div>
            </div>
            <div style={{ color: theme.textMuted, fontSize: '14px', maxWidth: '460px', textAlign: 'right', lineHeight: 1.5 }}>
              Not given to new clients automatically. A coach can assign any of these by hand.
            </div>
          </>
        ) : (
          <>
            <div>
              <div style={{ color: theme.textMuted, fontSize: '14px', marginBottom: '5px' }}>{secondTabLabel}</div>
              <div style={{ color: theme.gold, fontSize: '32px', fontWeight: 700 }}>{clientPrograms.length}</div>
            </div>
            <div>
              <div style={{ color: theme.textMuted, fontSize: '14px', marginBottom: '5px' }}>With client</div>
              <div style={{ color: theme.success, fontSize: '32px', fontWeight: 700 }}>{clientPrograms.filter(p => p.client_name).length}</div>
            </div>
            <div>
              <div style={{ color: theme.textMuted, fontSize: '14px', marginBottom: '5px' }}>Unassigned</div>
              <div style={{ color: theme.gold, fontSize: '32px', fontWeight: 700 }}>{clientPrograms.filter(p => !p.client_name).length}</div>
            </div>
          </>
        )}
      </div>

      {/* Programs List */}
      {visiblePrograms.length === 0 ? (
        <div style={{
          backgroundColor: theme.surface1,
          padding: '40px',
          borderRadius: '8px',
          textAlign: 'center',
          border: `1px solid ${theme.surface3}`
        }}>
          <div style={{ color: theme.textMuted, fontSize: '18px', marginBottom: '10px' }}>
            {activeTab === 'templates' ? 'No templates yet' : activeTab === 'extra' ? 'No extra programs' : `No programs in "${secondTabLabel}" yet`}
          </div>
          <div style={{ color: theme.textMuted, fontSize: '14px' }}>
            {activeTab === 'templates'
              ? 'The core templates appear here: one for every scenario a new client can describe at sign-up'
              : activeTab === 'extra'
                ? 'Templates outside the core set appear here. They are assigned by hand, never automatically'
                : 'Client programs appear here when you create a program directly for a specific client'
            }
          </div>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gap: '20px'
        }}>
          {sections.map(section => (
            <React.Fragment key={section.key}>
            {section.label && (
              <h2 style={{
                color: theme.gold,
                fontSize: '16px',
                fontWeight: '700',
                margin: '10px 0 0',
                paddingBottom: '8px',
                borderBottom: `1px solid ${theme.surface3}`
              }}>
                {section.label} ({section.programs.length})
              </h2>
            )}
            {section.programs.map(program => (
            <div
              key={program.id}
              style={{
                backgroundColor: theme.surface1,
                padding: '25px',
                borderRadius: '8px',
                border: `1px solid ${theme.surface3}`
              }}
            >
              {/* Program Header */}
              <div style={{ 
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '15px'
              }}>
                <div style={{ flex: 1 }}>
                  <h3 style={{ 
                    color: 'white',
                    fontSize: '20px',
                    fontWeight: 700,
                    marginBottom: '8px'
                  }}>
                    {program.name}
                  </h3>
                  
                  {program.description && (
                    <p style={{ 
                      color: theme.textMuted,
                      fontSize: '14px',
                      marginBottom: '10px'
                    }}>
                      {program.description}
                    </p>
                  )}

                  {/* Assignment Status — only claim "Assigned" when there's a real client_name;
                      a program with no user_id/client attached is genuinely unassigned, not
                      just missing a name to fall back on. */}
                  <div style={{
                    display: 'inline-block',
                    padding: '6px 12px',
                    borderRadius: '8px',
                    backgroundColor: program.is_template || !program.client_name ? theme.surface3 : '#2C5F2D',
                    color: program.is_template || !program.client_name ? theme.textMuted : theme.success,
                    fontSize: '12px',
                    fontWeight: 700,
                    marginBottom: '10px'
                  }}>
                    {program.is_template
                      ? 'Template Library'
                      : program.client_name
                        ? `Assigned to ${program.client_name}`
                        : 'Unassigned'
                    }
                  </div>

                  {/* Created Date */}
                  <div style={{ color: theme.textMuted, fontSize: '12px' }}>
                    Created: {new Date(program.created_at).toLocaleDateString()}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{ 
                display: 'flex',
                gap: '10px',
                flexWrap: 'wrap'
              }}>
                <button
                  onClick={() => onSelectProgram(program)}
                  style={{
                    backgroundColor: 'transparent',
                    border: `2px solid ${theme.gold}`,
                    color: theme.gold,
                    padding: '10px 20px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 700
                  }}
                >
                  View Details
                </button>

                <button
                  onClick={() => onGeneratePDF(program.id)}
                  style={{
                    backgroundColor: theme.gold,
                    border: 'none',
                    color: theme.surface1,
                    padding: '10px 20px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 700
                  }}
                >
                  Generate PDF
                </button>

                <button
                  onClick={() => cloneProgram(program, true)}
                  style={{
                    backgroundColor: 'rgba(212, 175, 55, 0.1)',
                    border: '1px solid rgba(212, 175, 55, 0.3)',
                    color: theme.gold,
                    padding: '10px 20px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                  }}
                >
                  Clone
                </button>

                {(program.created_by === myAuthId || role === 'super_admin') && (
                  <>
                    <button
                      onClick={() => handleEditClick(program)}
                      style={{
                        padding: '6px 14px',
                        background: 'transparent',
                        border: `1px solid ${theme.gold}`,
                        borderRadius: '8px',
                        color: theme.gold,
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer',
                      }}
                    >
                      Edit
                    </button>

                    <button
                      onClick={() => deleteProgram(program.id)}
                      style={{
                        backgroundColor: 'transparent',
                        border: `1px solid ${theme.danger}`,
                        color: theme.danger,
                        padding: '10px 20px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      Delete
                    </button>
                  </>
                )}
              </div>
            </div>
            ))}
            </React.Fragment>
          ))}
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ isOpen: false, programId: null })}
        onConfirm={handleConfirmDelete}
        title="Delete Program"
        message="Are you sure you want to delete this program? This action cannot be undone."
        confirmText="Delete"
        variant="danger"
      />

    </div>
  );
};

export default ProgramsList;
